/**
 * Query keys for the shared TanStack Query cache (prompt.md §40).
 *
 * WHY every key is workspace scoped: the cache lives across account and
 * workspace switches, and an unscoped key would let one workspace's rows be
 * served to another after a switch. Prefixing with the workspace id makes that
 * class of bug structurally impossible, and gives the invalidation plan a single
 * prefix to clear.
 */

import type { ConversationId, RunId, ScheduleId, WorkspaceId } from "@dayvise/domain";

export interface QueryScope {
  readonly workspaceId: WorkspaceId | null;
}

export const queryKeys = {
  /** Everything belonging to one workspace, for a wholesale invalidation. */
  workspace: (workspaceId: WorkspaceId | null) => ["workspace", workspaceId ?? "none"] as const,

  conversations: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "conversations"] as const,
  conversation: (scope: QueryScope, conversationId: ConversationId) =>
    [...queryKeys.workspace(scope.workspaceId), "conversation", conversationId] as const,
  conversationOutputs: (scope: QueryScope, conversationId: ConversationId, branchId: string | null) =>
    [...queryKeys.conversation(scope, conversationId), "outputs", branchId ?? "default"] as const,
  messages: (scope: QueryScope, conversationId: ConversationId, branchId: string | null) =>
    [...queryKeys.conversation(scope, conversationId), "messages", branchId ?? "default"] as const,

  runs: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "runs"] as const,
  run: (scope: QueryScope, runId: RunId) => [...queryKeys.workspace(scope.workspaceId), "run", runId] as const,
  runEvents: (scope: QueryScope, runId: RunId) => [...queryKeys.run(scope, runId), "events"] as const,
  runApprovals: (scope: QueryScope, runId: RunId) => [...queryKeys.run(scope, runId), "approvals"] as const,
  runDiff: (scope: QueryScope, runId: RunId) => [...queryKeys.run(scope, runId), "diff"] as const,

  agents: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "agents"] as const,
  agent: (scope: QueryScope, agentId: string) => [...queryKeys.agents(scope), agentId] as const,

  schedules: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "schedules"] as const,
  schedule: (scope: QueryScope, scheduleId: ScheduleId) => [...queryKeys.schedules(scope), scheduleId] as const,

  memory: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "memory"] as const,
  privateMemory: (scope: QueryScope) => [...queryKeys.memory(scope), "private"] as const,
  sharedMemory: (scope: QueryScope) => [...queryKeys.memory(scope), "shared"] as const,

  files: (scope: QueryScope, prefix?: string) => [...queryKeys.workspace(scope.workspaceId), "files", prefix ?? ""] as const,

  credentials: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "credentials"] as const,
  usage: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "usage"] as const,
  refinements: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "refinements"] as const,
  refinement: (scope: QueryScope, refinementId: string) => [...queryKeys.refinements(scope), refinementId] as const,
  entitlements: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "entitlements"] as const,
  tools: (scope: QueryScope) => [...queryKeys.entitlements(scope), "tools"] as const,
  billing: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "billing"] as const,
  notifications: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "notifications"] as const,
  audit: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "audit"] as const,

  mcpServers: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "mcpServers"] as const,
  presence: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "presence"] as const,
  boards: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "boards"] as const,
  yolo: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "yolo"] as const,
  board: (scope: QueryScope, boardId: string) => [...queryKeys.boards(scope), boardId] as const,
  callPreferences: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "callPreferences"] as const,
  /** The durable pending-input list; a new question on any device lands here. */
  inputRequests: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "inputRequests"] as const,
  devices: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "devices"] as const,
  home: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "home"] as const,
  mcpServer: (scope: QueryScope, serverId: string) => [...queryKeys.mcpServers(scope), serverId] as const,
  mcpTools: (scope: QueryScope, serverId: string) => [...queryKeys.mcpServer(scope, serverId), "tools"] as const,

  skills: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "skills"] as const,
  skill: (scope: QueryScope, skillId: string) => [...queryKeys.skills(scope), skillId] as const,
  skillShares: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "skillShares"] as const,
  skillShareTargets: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "skillShareTargets"] as const,
  snapshots: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "snapshots"] as const,
  automationTemplates: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "automationTemplates"] as const,
  skillBundles: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "skillBundles"] as const,
  learningEvents: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "learningEvents"] as const,

  adminMembers: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "admin", "members"] as const,
  adminSharedMemories: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "admin", "sharedMemories"] as const,

  storageRoute: (scope: QueryScope) => [...queryKeys.workspace(scope.workspaceId), "storage", "route"] as const,
  storageMigrationJob: (scope: QueryScope, jobId: string) => [...queryKeys.workspace(scope.workspaceId), "storage", "migration", jobId] as const,
} as const;

/**
 * Map an invalidation plan (`session.ts`) onto the query-key prefixes to reset.
 * Anything not listed is left alone, so a workspace switch does not needlessly
 * drop the user's UI preferences.
 */
export function keyspacesToQueryRoots(keyspaces: readonly string[], workspaceId: WorkspaceId | null): readonly (readonly unknown[])[] {
  const prefixes: Record<string, readonly unknown[]> = {
    conversations: queryKeys.conversations({ workspaceId }),
    messages: queryKeys.conversations({ workspaceId }),
    runs: queryKeys.runs({ workspaceId }),
    runEvents: queryKeys.runs({ workspaceId }),
    memories: queryKeys.memory({ workspaceId }),
    files: queryKeys.files({ workspaceId }),
    schedules: queryKeys.schedules({ workspaceId }),
    credentials: queryKeys.credentials({ workspaceId }),
    usage: queryKeys.usage({ workspaceId }),
    entitlements: queryKeys.entitlements({ workspaceId }),
    toolPolicy: queryKeys.entitlements({ workspaceId }),
    storageRoute: queryKeys.entitlements({ workspaceId }),
    notifications: queryKeys.notifications({ workspaceId }),
    audit: queryKeys.audit({ workspaceId }),
    mcpServers: queryKeys.mcpServers({ workspaceId }),
    skills: queryKeys.skills({ workspaceId }),
  };
  const roots = new Set<string>();
  const result: (readonly unknown[])[] = [];
  for (const keyspace of keyspaces) {
    const root = prefixes[keyspace];
    if (!root) continue;
    const signature = JSON.stringify(root);
    if (roots.has(signature)) continue;
    roots.add(signature);
    result.push(root);
  }
  return result;
}
